import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { PaymentMethodUtilService } from '@amfam/billing/payment-method/util/lib/services/payment-method-util.service';

import {
  AddEditPaymentMethodResponse,
  GetPaymentMethodsResponse,
  UpdateModeOfAuthorizationResponse
} from '../../models/payment-method.model';
import { PaymentMethodService } from '../../services/payment-method.service';
import {
  addPaymentMethodFailure,
  addPaymentMethodSuccess,
  deletePaymentMethodFailure,
  deletePaymentMethodSuccess,
  editPaymentMethodFailure,
  editPaymentMethodSuccess,
  getPaymentMethodsFailure,
  getPaymentMethodsSuccess,
  addPaymentMethod as PaymentMethodActionsAddPaymentMethod,
  deletePaymentMethod as PaymentMethodActionsDeletePaymentMethod,
  editPaymentMethod as PaymentMethodActionsEditPaymentMethod,
  getPaymentMethods as PaymentMethodActionsGetPaymentMethods,
  updateModeOfAuthorization as PaymentMethodActionsUpdateModeOfAuthorization,
  sendPaymentMethodAnalytics,
  updateModeOfAuthorizationFailure,
  updateModeOfAuthorizationSuccess
} from './payment-method.actions';

export const getPaymentMethods = createEffect(
  (actions$ = inject(Actions), paymentMethodService = inject(PaymentMethodService)) => {
    return actions$.pipe(
      ofType(PaymentMethodActionsGetPaymentMethods),
      switchMap(action =>
        paymentMethodService.getPaymentMethods().pipe(
          map((response: GetPaymentMethodsResponse) =>
            getPaymentMethodsSuccess({
              paymentMethods: response.paymentAccounts,
              correlationId: action.correlationId
            })
          ),
          catchError(error =>
            of(
              getPaymentMethodsFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const addPaymentMethod = createEffect(
  (actions$ = inject(Actions), paymentMethodService = inject(PaymentMethodService)) => {
    return actions$.pipe(
      ofType(PaymentMethodActionsAddPaymentMethod),
      switchMap(action =>
        paymentMethodService.addPaymentMethod(action.payload).pipe(
          map((response: AddEditPaymentMethodResponse) =>
            addPaymentMethodSuccess({
              response,
              correlationId: action.correlationId
            })
          ),
          catchError(error =>
            of(
              addPaymentMethodFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const editPaymentMethod = createEffect(
  (actions$ = inject(Actions), paymentMethodService = inject(PaymentMethodService)) => {
    return actions$.pipe(
      ofType(PaymentMethodActionsEditPaymentMethod),
      switchMap(action =>
        paymentMethodService.editPaymentMethod(action.payload, action.paymentAccountId).pipe(
          map((response: AddEditPaymentMethodResponse) =>
            editPaymentMethodSuccess({
              response,
              correlationId: action.correlationId,
              oldPaymentAccountId: action.paymentAccountId,
              newNickName: action.payload.paymentAccount.nickName ?? '',
              oldNickName: action.payload.paymentAccount.oldNickName ?? ''
            })
          ),
          catchError(error =>
            of(
              editPaymentMethodFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const updateModeOfAuthorization = createEffect(
  (actions$ = inject(Actions), paymentMethodService = inject(PaymentMethodService)) => {
    return actions$.pipe(
      ofType(PaymentMethodActionsUpdateModeOfAuthorization),
      switchMap(action => {
        const responses: Observable<UpdateModeOfAuthorizationResponse>[] = action.payload.map(
          request => paymentMethodService.updateModeOfAuthorization(request)
        );
        return forkJoin(responses).pipe(
          map((res: UpdateModeOfAuthorizationResponse[]) => {
            return updateModeOfAuthorizationSuccess({
              response: res,
              correlationId: action.correlationId
            });
          }),
          catchError(error =>
            of(
              updateModeOfAuthorizationFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        );
      })
    );
  },
  { functional: true }
);

export const deletePaymentMethod = createEffect(
  (actions$ = inject(Actions), paymentMethodService = inject(PaymentMethodService)) => {
    return actions$.pipe(
      ofType(PaymentMethodActionsDeletePaymentMethod),
      switchMap(action =>
        paymentMethodService.deletePaymentMethod(action.paymentAccountId).pipe(
          map(response =>
            deletePaymentMethodSuccess({
              response,
              correlationId: action.correlationId,
              paymentAccountId: action.paymentAccountId
            })
          ),
          catchError(error =>
            of(
              deletePaymentMethodFailure({
                error,
                correlationId: action.correlationId,
                paymentAccountId: action.paymentAccountId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const analyticsNotification = createEffect(
  (actions$ = inject(Actions), paymentMethodUtil = inject(PaymentMethodUtilService)) => {
    return actions$.pipe(
      ofType(sendPaymentMethodAnalytics),
      map(action => paymentMethodUtil.sendPaymentMethodAnalytics(action.payload))
    );
  },
  { dispatch: false, functional: true }
);
